import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Pencil, Users as UsersIcon, Trash2, Search } from 'lucide-react';
import dayjs from 'dayjs';
import {
  CreateNooraniGroupPayload,
  NooraniGroupDetail,
  NooraniGroupListRow,
  NooraniGroupsApi,
  NooraniStudentPickerRow,
  UpdateNooraniGroupPayload,
} from '@/services/noorani-groups.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<NooraniGroupListRow>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Noorani Groups — ad-hoc, cross-class student groupings for Qaidathul
 * Nooraniyya/Quran instruction. Create/edit/delete + teacher assignment is
 * Head Office only (org/superadmin scope); the owning school gets read
 * access to its own groups plus student-membership management (both scopes
 * can open "Manage Students"). One page, three scopes — same pattern as
 * MaterialRequestsPage.
 */
export function NooraniGroupsPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  const showSchoolPicker = scope !== 'school';
  const canManageGroup = scope === 'superadmin' || scope === 'organization';

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<NooraniGroupListRow | null>(null);
  const [managingStudents, setManagingStudents] = useState<NooraniGroupListRow | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; group?: NooraniGroupListRow }>({ open: false });
  const [schoolFilter, setSchoolFilter] = useState('');
  const [errorNotice, setErrorNotice] = useState<string | null>(null);

  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolFilter]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-noorani-groups-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['noorani-groups', { ...dt.params, schoolFilter, scope }],
    queryFn: () =>
      NooraniGroupsApi.list({
        ...dt.params,
        schoolId: showSchoolPicker ? schoolFilter || undefined : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['noorani-groups'] });

  const remove = useMutation({
    mutationFn: (id: string) => NooraniGroupsApi.remove(id),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
    onError: (e) => setErrorNotice(errMsg(e) ?? 'Could not delete group'),
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'no',
        header: 'No.',
        enableSorting: false,
        cell: (info) => (
          <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
        ),
      }),
      columnHelper.accessor('name', { id: 'name', header: 'Group Name' }),
      ...(showSchoolPicker
        ? [columnHelper.accessor('schoolName', { id: 'schoolName', header: 'School' })]
        : []),
      columnHelper.display({
        id: 'teacherName',
        header: 'Assigned Teacher',
        cell: (info) => info.row.original.teacherName ?? <span className="text-slate-400">Unassigned</span>,
      }),
      columnHelper.accessor('studentCount', {
        id: 'studentCount',
        header: 'Students Assigned',
        cell: (info) => <Badge tone={info.getValue() > 0 ? 'green' : 'amber'}>{info.getValue()}</Badge>,
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Created',
        cell: (info) => dayjs(info.getValue()).format('DD MMM YYYY'),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const g = info.row.original;
          return (
            <div className="inline-flex items-center gap-1">
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600"
                onClick={() => setManagingStudents(g)}
                title="Manage students"
              >
                <UsersIcon className="h-4 w-4" />
              </button>
              {canManageGroup && (
                <>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => setEditing(g)}
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setConfirm({ open: true, group: g })}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          );
        },
      }),
    ],
    [showSchoolPicker, canManageGroup, dt.pagination.pageIndex, dt.pagination.pageSize],
  );

  return (
    <>
      <PageHeader
        title="Noorani Groups"
        description={
          scope === 'school'
            ? 'Your Noorani (Qaidathul Nooraniyya) groups — manage which students are assigned.'
            : 'Cross-class student groupings for Qaidathul Nooraniyya instruction. Create a group for a school and assign it a teacher.'
        }
        actions={
          canManageGroup && (
            <button className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> New Group
            </button>
          )
        }
      />

      {errorNotice && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{errorNotice}</span>
          <button onClick={() => setErrorNotice(null)} className="text-red-600 hover:text-red-800">
            ✕
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No Noorani groups yet."
        toolbar={
          showSchoolPicker && (
            <Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} className="sm:w-56">
              <option value="">All schools</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )
        }
      />

      {canManageGroup && (
        <GroupFormModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          schools={scope === 'organization' || scope === 'superadmin' ? schools : undefined}
          onSaved={invalidate}
        />
      )}

      {editing && (
        <GroupFormModal
          open
          onClose={() => setEditing(null)}
          group={editing}
          onSaved={invalidate}
        />
      )}

      {managingStudents && (
        <ManageStudentsModal group={managingStudents} onClose={() => setManagingStudents(null)} onSaved={invalidate} />
      )}

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.group && remove.mutate(confirm.group.id)}
        loading={remove.isPending}
        title="Delete Noorani group?"
        message="This removes the group and every student's membership in it. This cannot be undone."
        confirmText="Delete"
      />
    </>
  );
}

function GroupFormModal({
  open,
  onClose,
  onSaved,
  group,
  schools,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Present when editing an existing group — school becomes fixed, name/teacher editable. */
  group?: NooraniGroupListRow;
  /** Present only for create, when the caller (org/superadmin) must pick a school. */
  schools?: { id: string; name: string }[];
}) {
  const isEdit = !!group;
  const [schoolId, setSchoolId] = useState(group?.schoolId ?? '');
  const [name, setName] = useState(group?.name ?? '');
  const [teacherId, setTeacherId] = useState(group?.teacherId ?? '');

  useEffect(() => {
    if (open) {
      setSchoolId(group?.schoolId ?? '');
      setName(group?.name ?? '');
      setTeacherId(group?.teacherId ?? '');
    }
  }, [open, group]);

  const effectiveSchoolId = isEdit ? group!.schoolId : schoolId;
  const { data: teachers = [] } = useQuery({
    queryKey: ['noorani-teachers-picker', effectiveSchoolId],
    queryFn: () => NooraniGroupsApi.teachersPicker(effectiveSchoolId || undefined),
    enabled: open && !!effectiveSchoolId,
  });

  const create = useMutation({
    mutationFn: (payload: CreateNooraniGroupPayload) => NooraniGroupsApi.create(payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const update = useMutation({
    mutationFn: (payload: UpdateNooraniGroupPayload) => NooraniGroupsApi.update(group!.id, payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const saving = create.isPending || update.isPending;
  const error = errMsg(create.error) ?? errMsg(update.error);

  const valid = name.trim().length > 0 && (isEdit || !schools || !!schoolId);

  const submit = () => {
    if (isEdit) {
      update.mutate({ name: name.trim(), teacherId: teacherId || null });
    } else {
      create.mutate({ schoolId: schools ? schoolId : undefined, name: name.trim(), teacherId: teacherId || undefined });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Noorani Group' : 'Create Noorani Group'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={saving || !valid}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create group'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {!isEdit && schools && (
          <Field label="School" required>
            <Select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setTeacherId(''); }}>
              <option value="">— Select school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Group Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Group A - Morning Batch" />
        </Field>
        <Field label="Assigned Teacher">
          <Select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} disabled={!effectiveSchoolId}>
            <option value="">— Unassigned —</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      </div>
    </Modal>
  );
}

function ManageStudentsModal({
  group,
  onClose,
  onSaved,
}: {
  group: NooraniGroupListRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [originallyAssigned, setOriginallyAssigned] = useState<Set<string>>(new Set());

  const { data: detail } = useQuery({
    queryKey: ['noorani-group-detail', group.id],
    queryFn: () => NooraniGroupsApi.get(group.id),
  });

  useEffect(() => {
    if (detail) {
      const ids = new Set(detail.students.map((s) => s.id));
      setChecked(ids);
      setOriginallyAssigned(ids);
    }
  }, [detail]);

  const { data: classes = [] } = useQuery({
    queryKey: ['noorani-classes-picker', group.schoolId],
    queryFn: () => NooraniGroupsApi.classesPicker(group.schoolId),
  });
  const sections = classes.filter((c) => c.classId === classId);

  const { data: students = [], isFetching } = useQuery({
    queryKey: ['noorani-students-picker', group.schoolId, classId, sectionId, search],
    queryFn: () => NooraniGroupsApi.studentsPicker({ schoolId: group.schoolId, classId: classId || undefined, sectionId: sectionId || undefined, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const sync = useMutation({
    mutationFn: () => {
      const add = [...checked].filter((id) => !originallyAssigned.has(id));
      const remove = [...originallyAssigned].filter((id) => !checked.has(id));
      return NooraniGroupsApi.syncStudents(group.id, { add: add.length ? add : undefined, remove: remove.length ? remove : undefined });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const toggle = (row: NooraniStudentPickerRow) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const dirty = useMemo(() => {
    if (checked.size !== originallyAssigned.size) return true;
    for (const id of checked) if (!originallyAssigned.has(id)) return true;
    return false;
  }, [checked, originallyAssigned]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Manage Students"
      description={`${group.name} — ${group.schoolName}`}
      size="xl"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => sync.mutate()} disabled={sync.isPending || !dirty}>
            {sync.isPending ? 'Saving…' : `Save (${checked.size} selected)`}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or admission #" className="pl-9" />
          </div>
          <Select className="!w-40" value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}>
            <option value="">All classes</option>
            {[...new Map(classes.map((c) => [c.classId, c.className])).entries()].map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>
          <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
            <option value="">All sections</option>
            {sections.filter((s) => s.sectionId).map((s) => (
              <option key={s.sectionId!} value={s.sectionId!}>
                {s.sectionName}
              </option>
            ))}
          </Select>
        </div>

        <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="w-10 py-2 pl-3" />
                <th className="py-2 pr-3">Student</th>
                <th className="py-2 pr-3">Admission #</th>
                <th className="py-2 pr-3">Class</th>
                <th className="py-2 pr-3">Current Group</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!isFetching && students.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-400">
                    No students match these filters.
                  </td>
                </tr>
              )}
              {students.map((s) => {
                const isChecked = checked.has(s.id);
                const inOtherGroup = s.assignedGroupId && s.assignedGroupId !== group.id;
                return (
                  <tr key={s.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pl-3">
                      <input type="checkbox" checked={isChecked} onChange={() => toggle(s)} />
                    </td>
                    <td className="py-2 pr-3">{s.name}</td>
                    <td className="py-2 pr-3">{s.admissionNo}</td>
                    <td className="py-2 pr-3">
                      {s.className} - {s.sectionName}
                    </td>
                    <td className="py-2 pr-3">
                      {inOtherGroup ? (
                        <span className="text-xs text-amber-600">In another group — checking moves them here</span>
                      ) : s.assignedGroupId === group.id ? (
                        <Badge tone="green">This group</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Unassigned</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sync.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(sync.error)}</div>}
      </div>
    </Modal>
  );
}
